import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  Fade,
  InputAdornment,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Subject as SubjectIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Sort as SortIcon
} from "@mui/icons-material";
import { AuthContext } from "../store/AuthContext";
import { getSubjects } from "../api/classSubjectApi";

const BASE_URL = "https://lms.childinfo.in/api";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function TopicsManager() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { token, user } = useContext(AuthContext);

  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    topicId: null,
    topicName: ""
  });

  const [form, setForm] = useState({
    subject_id: "",
    topic_name: "",
    description: "",
    status: "Active"
  });

  // =========================
  // LOAD DATA
  // =========================
  useEffect(() => {
    loadTopics();
    loadSubjects();
  }, []);

  useEffect(() => {
    filterAndSortTopics();
  }, [topics, searchTerm, subjectFilter, statusFilter, sortBy]);

  const loadTopics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/topics.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopics(res.data || []);
    } catch (error) {
      showSnackbar("Failed to load topics", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const res = await getSubjects(token);
      setSubjects(res.data || []);
    } catch (error) {
      showSnackbar("Failed to load subjects", "error");
    }
  };

  // =========================
  // FILTERING & SORTING
  // =========================
  const filterAndSortTopics = () => {
    let filtered = [...topics];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(topic =>
        topic.topic_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        topic.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Subject filter
    if (subjectFilter !== "all") {
      filtered = filtered.filter(topic => topic.subject_id === parseInt(subjectFilter));
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(topic => topic.status === statusFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.topic_name.localeCompare(b.topic_name);
        case "status":
          return a.status.localeCompare(b.status);
        case "subject":
          const subjectA = subjects.find(s => s.id === a.subject_id)?.subject || "";
          const subjectB = subjects.find(s => s.id === b.subject_id)?.subject || "";
          return subjectA.localeCompare(subjectB);
        default:
          return 0;
      }
    });

    setFilteredTopics(filtered);
  };

  // =========================
  // FORM HANDLERS
  // =========================
  const resetForm = () => {
    setEditingId(null);
    setShowForm(false);
    setForm({
      subject_id: "",
      topic_name: "",
      description: "",
      status: "Active"
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const startEdit = (topic) => {
    setEditingId(topic.id);
    setShowForm(true);
    setForm({
      subject_id: topic.subject_id,
      topic_name: topic.topic_name,
      description: topic.description || "",
      status: topic.status
    });
  };

  // =========================
  // CRUD OPERATIONS
  // =========================
  const saveTopic = async () => {
    if (!form.subject_id || !form.topic_name) {
      showSnackbar("Subject and Topic Name are required", "error");
      return;
    }

    setSaving(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      };

      if (editingId) {
        await axios.put(
          `${BASE_URL}/topics.php?id=${editingId}`,
          form,
          config
        );
        showSnackbar("Topic updated successfully", "success");
      } else {
        await axios.post(
          `${BASE_URL}/topics.php`,
          form,
          config
        );
        showSnackbar("Topic added successfully", "success");
      }

      resetForm();
      loadTopics();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Failed to save topic", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteTopic = async () => {
    try {
      await axios.delete(`${BASE_URL}/topics.php?id=${deleteDialog.topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar("Topic deleted successfully", "success");
      loadTopics();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Failed to delete topic", "error");
    } finally {
      setDeleteDialog({ open: false, topicId: null, topicName: "" });
    }
  };

  // =========================
  // UTILITIES
  // =========================
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject?.subject || "Unknown Subject";
  };

  const getSubjectColor = (subjectId) => {
    const colors = [
      "#4CAF50", "#2196F3", "#FF9800", "#E91E63", 
      "#9C27B0", "#00BCD4", "#FF5722", "#795548"
    ];
    const index = subjectId % colors.length;
    return colors[index];
  };

  // =========================
  // UI COMPONENTS
  // =========================
  const ActionButtons = ({ topic }) => {
    if (user.role === "student") return null;

    return (
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
        <Tooltip title="Edit">
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={(e) => {
              e.stopPropagation();
              startEdit(topic);
            }}
            variant="outlined"
            color="primary"
          >
            Edit
          </Button>
        </Tooltip>
        <Tooltip title="Delete">
          <Button
            size="small"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialog({
                open: true,
                topicId: topic.id,
                topicName: topic.topic_name
              });
            }}
            variant="outlined"
            color="error"
          >
            Delete
          </Button>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box sx={{
      background: "#f5f7fa",
      minHeight: "100vh",
      p: { xs: 2, sm: 3 },
    }}>
      {/* HEADER */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ 
          color: "#2c3e50",
          mb: 1
        }}>
          Topics Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and organize topics for your subjects
        </Typography>
      </Box>

      {/* CONTROLS BAR */}
      <Paper sx={{
        background: "white",
        p: 3,
        mb: 3,
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
      }}>
        <Grid container spacing={2} alignItems="center">
          {/* SEARCH */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search topics by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#f8f9fa",
                  "&:hover fieldset": {
                    borderColor: "#2196F3",
                  },
                },
              }}
            />
          </Grid>

          {/* FILTERS */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              label="Filter by Subject"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SubjectIcon sx={{ color: "text.secondary", mr: 1 }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">All Subjects</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.subject}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort by"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SortIcon sx={{ color: "text.secondary", mr: 1 }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="name">Name (A-Z)</MenuItem>
              <MenuItem value="subject">Subject</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </TextField>
          </Grid>

          {/* ADD BUTTON */}
          {(user.role === "admin" || user.role === "teacher") && (
            <Grid item xs={12} md={1}>
              <Tooltip title="Add New Topic">
                <Button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  sx={{
                    background: "linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)",
                    color: "white",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1976D2 0%, #0D8BF2 100%)",
                    },
                    boxShadow: "0 2px 8px rgba(33, 150, 243, 0.3)"
                  }}
                >
                  <AddIcon />
                  Add Topic
                </Button>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ADD/EDIT FORM */}
      <Fade in={showForm}>
        <Box>
          {(user.role === "admin" || user.role === "teacher") && showForm && (
            <Paper sx={{
              background: "white",
              p: 3,
              mb: 4,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
            }}>
              <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                mb: 3,
                pb: 2,
                borderBottom: "1px solid #eee"
              }}>
                <Typography variant="h6" sx={{ color: "#2c3e50", fontWeight: 600 }}>
                  <Box component="span" sx={{ 
                    background: "linear-gradient(135deg, #2196F3, #21CBF3)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mr: 1
                  }}>
                    {editingId ? "✏️ Edit Topic" : "➕ Create New Topic"}
                  </Box>
                </Typography>
                <IconButton onClick={resetForm} sx={{ color: "#666" }}>
                  <CancelIcon />
                </IconButton>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    required
                    label="Subject"
                    name="subject_id"
                    value={form.subject_id}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SubjectIcon sx={{ color: "#4CAF50" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "#f8f9fa",
                      },
                    }}
                  >
                    {subjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.subject}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    label="Topic Name"
                    name="topic_name"
                    value={form.topic_name}
                    onChange={handleChange}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "#f8f9fa",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Enter a detailed description of the topic..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon sx={{ 
                            color: "#FF9800",
                            alignSelf: "flex-start",
                            mt: 1 
                          }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "#f8f9fa",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Status"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    fullWidth
                  >
                    <MenuItem value="Active">
                      <Chip size="small" label="Active" color="success" variant="outlined" />
                    </MenuItem>
                    <MenuItem value="Inactive">
                      <Chip size="small" label="Inactive" color="default" variant="outlined" />
                    </MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      sx={{
                        borderColor: "#ddd",
                        color: "#666",
                        "&:hover": {
                          borderColor: "#999",
                          background: "#f5f5f5"
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={saveTopic}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      sx={{
                        background: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: 120,
                        "&:hover": {
                          background: "linear-gradient(135deg, #43A047 0%, #1B5E20 100%)",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)"
                        },
                        "&.Mui-disabled": {
                          background: "#e0e0e0",
                        }
                      }}
                    >
                      {saving ? "Saving..." : editingId ? "Update Topic" : "Save Topic"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </Fade>

      {/* TOPICS LIST */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Box textAlign="center">
            <CircularProgress sx={{ color: "#2196F3", mb: 2 }} />
            <Typography color="text.secondary">Loading topics...</Typography>
          </Box>
        </Box>
      ) : filteredTopics.length === 0 ? (
        <Paper sx={{
          background: "white",
          p: 6,
          textAlign: "center",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
        }}>
          <Box sx={{ fontSize: 48, mb: 2, color: "#e0e0e0" }}>
            📚
          </Box>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || subjectFilter !== "all" || statusFilter !== "all" 
              ? "No topics match your search criteria" 
              : "No topics available"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm ? "Try a different search term" : 
             subjectFilter !== "all" ? "Try selecting a different subject" :
             "Start by adding your first topic"}
          </Typography>
          {(user.role === "admin" || user.role === "teacher") && !showForm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
              sx={{
                background: "linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1976D2 0%, #0D8BF2 100%)",
                }
              }}
            >
              Add New Topic
            </Button>
          )}
        </Paper>
      ) : (
        <Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredTopics.length} topic{filteredTopics.length !== 1 ? 's' : ''}
          </Typography>
          <Grid container spacing={3}>
            {filteredTopics.map((topic) => {
              const subjectName = getSubjectName(topic.subject_id);
              const subjectColor = getSubjectColor(topic.subject_id);
              
              return (
                <Grid item xs={12} sm={6} lg={4} key={topic.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      border: "1px solid #e0e0e0",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                        borderColor: subjectColor
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "flex-start",
                        mb: 2 
                      }}>
                        <Box>
                          <Chip
                            label={subjectName}
                            size="small"
                            sx={{
                              background: `${subjectColor}15`,
                              color: subjectColor,
                              border: `1px solid ${subjectColor}30`,
                              fontWeight: 500,
                              mb: 1
                            }}
                          />
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: "#2c3e50",
                              fontWeight: 600,
                              mt: 1
                            }}
                          >
                            {topic.topic_name}
                          </Typography>
                        </Box>
                        <Chip
                          label={topic.status}
                          size="small"
                          color={topic.status === "Active" ? "success" : "default"}
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                      </Box>
                      
                      {topic.description && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mb: 2,
                            lineHeight: 1.6
                          }}
                        >
                          {topic.description}
                        </Typography>
                      )}
                      
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1, 
                        mt: "auto",
                        pt: 2,
                        borderTop: "1px solid #f0f0f0"
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: {topic.id}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                    
                    {(user.role === "admin" || user.role === "teacher") && (
                      <CardActions sx={{ 
                        borderTop: "1px solid #f0f0f0",
                        background: "#f9f9f9"
                      }}>
                        <ActionButtons topic={topic} />
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialog.open}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setDeleteDialog({ open: false, topicId: null, topicName: "" })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ color: "#d32f2f", fontWeight: 600 }}>
          🗑️ Delete Topic
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete the topic <strong>"{deleteDialog.topicName}"</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All associated content will be removed.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, topicId: null, topicName: "" })}
            variant="outlined"
            sx={{ borderColor: "#ddd" }}
          >
            Cancel
          </Button>
          <Button
            onClick={deleteTopic}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              background: "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #c62828 0%, #9a0007 100%)"
              }
            }}
          >
            Delete Topic
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ minWidth: 300, borderRadius: 1 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}